<script setup>
import { AbcHeading } from "@ordbogen-npm/abc-dsk";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import ImageResponsive from "@/components/ImageResponsive.vue";
import QuizJumbotron from "@/components/vocabularyTrainer/QuizJumbotron.vue";
import WordListItemGrid from "@/components/vocabularyTrainer/WordListItemGrid.vue";
import { useSettingsStore } from "@/stores";

const props = defineProps({
  wrongAnswers: {
    type: Array,
    required: true
  }
});
const emit = defineEmits(["next"]);
const { t } = useI18n();
const settingsStore = useSettingsStore();

const heading = computed(() => {
  const activeLanguageGroup = settingsStore.activeLanguageGroup;
  if (activeLanguageGroup === "english") {
    return t("vocab.quizEnd.jumbotronHeading.english");
  }

  if (activeLanguageGroup === "german") {
    return t("vocab.quizEnd.jumbotronHeading.german");
  }
  return "";
});

const suggestions = computed(() =>
  props.wrongAnswers.map((a) => ({
    ...a,
    deletable: false
  }))
);

// Properties calculated for CSS
const breakpointColumns = (itemsCount, max) => (itemsCount > max ? max : itemsCount);
const gridColumns = computed(() => ({
  sm: breakpointColumns(suggestions.value.length, 2),
  md: breakpointColumns(suggestions.value.length, 3)
}));
</script>

<template>
  <QuizJumbotron>
    <div class="container">
      <div class="row jumbotron-inner">
        <AbcHeading tag="h1" size="xl">{{ heading }}</AbcHeading>
      </div>
    </div>
  </QuizJumbotron>

  <div class="quiz-end-content container">
    <ImageResponsive
      src="/assets/img/vocab/happy-boy@1x.png"
      class="quiz-end-content-image"
      img-class="quiz-end-content-image-inner"
      presentational
    />

    <div v-if="suggestions.length" class="row">
      <AbcHeading class="quiz-end-content-heading col-12" tag="h2" size="md">
        {{ t("vocab.quizEnd.bodyHeading") }}
      </AbcHeading>
    </div>
    <div class="row">
      <div class="col-12 d-flex justify-content-center">
        <WordListItemGrid :sm="gridColumns.sm" :md="gridColumns.md" :items="suggestions" />
      </div>
    </div>
  </div>

  <div class="quiz-end-button-container container">
    <div class="row">
      <TgButton size="lg" @click="emit('next')">
        {{ t("vocab.quizEnd.continue") }}
      </TgButton>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.jumbotron-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 86px 0 109px;
}

.quiz-end-content {
  display: flex;
  flex-direction: column;
  gap: 35px;
  margin: 24px auto;

  &-image {
    display: flex;
    position: relative;
    justify-content: center;
    margin: -87px 0 30px;
  }

  :deep(&-image-inner) {
    max-width: 150px;

    #media-breakpoint-up(sm, {
      max-width: initial;
    });
  }

  &-heading {
    text-align: center;

    #media-breakpoint-down(sm, {
      font-weight: 400;
      color: rgb(@theme-colors[@grey][base]);
      font-size: 1rem;
    });
  }
}

.quiz-end-button-container {
  display: flex;
  justify-content: center;
  margin: 20px auto 44px;
}
</style>
